import { useQuery } from '@tanstack/react-query'
import { CalendarClock, ClipboardList } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { errorMessage } from '@/api/errors'
import { ErrorState, LoadingState } from '@/components/feedback/States'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { useAuth } from '@/features/auth/useAuth'
import { submissionsQuery } from '@/features/lms/api'
import { FileChips } from '@/features/lms/components/FileChips'
import { GradeDialog } from '@/features/lms/components/GradeDialog'
import { RichText } from '@/features/lms/components/RichText'
import { formatPoints, lmsBase, workStatus, workStatusMeta } from '@/features/lms/status'
import type { SubmissionRoster } from '@/features/lms/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { formatDateTime } from '@/utils/format'

type Row = SubmissionRoster['rows'][number]

/** An assignment's hand-ins: the whole class, who handed in (late or not), and grading. */
export function LmsAssignmentPage() {
  const { t } = useTranslation('lms')
  const { portal } = useAuth()
  const id = Number(useParams().id)
  const roster = useQuery(submissionsQuery(id))
  const [grading, setGrading] = useState<Row | null>(null)
  const base = lmsBase(portal)
  useDocumentTitle(roster.data?.assignment.title ?? 'Assignment')

  const data = roster.data
  const columns = useMemo<Column<Row>[]>(() => {
    if (!data) return []
    const a = data.assignment
    return [
      {
        key: 'student',
        header: t('Student'),
        cell: (r) => (
          <div className="flex items-center gap-3">
            <Avatar name={r.student.name} src={r.student.photoUrl} size="sm" />
            <div className="min-w-0">
              <p className="truncate font-medium text-ink">{r.student.name}</p>
              <p className="text-xs text-muted">{r.student.code}</p>
            </div>
          </div>
        ),
      },
      {
        key: 'status',
        header: t('Status'),
        cell: (r) => {
          const s = workStatusMeta[workStatus(a, r.submission)]
          return (
            <Badge tone={s.tone} size="sm">
              {t(s.label)}
            </Badge>
          )
        },
      },
      {
        key: 'submitted',
        header: t('Handed in'),
        hideBelow: 'md',
        cell: (r) =>
          r.submission ? formatDateTime(r.submission.submittedAt) : <span className="text-muted">—</span>,
      },
      {
        key: 'points',
        header: a.maxPoints ? t('Points') : t('Feedback'),
        align: a.maxPoints ? 'right' : 'left',
        cell: (r) =>
          a.maxPoints ? (
            <span className="tabular">{formatPoints(r.submission?.points ?? null, a.maxPoints)}</span>
          ) : r.submission?.feedback ? (
            <span className="line-clamp-1 text-sm">{r.submission.feedback}</span>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        key: 'action',
        header: <span className="sr-only">{t('Actions')}</span>,
        align: 'right',
        cell: (r) =>
          r.submission ? (
            <Button
              size="sm"
              variant={r.submission.gradedAt ? 'ghost' : 'secondary'}
              onClick={() => setGrading(r)}
            >
              {r.submission.gradedAt ? t('Review') : t('Grade')}
            </Button>
          ) : null,
      },
    ]
  }, [data, t])

  if (roster.isPending) return <LoadingState />
  if (roster.error)
    return <ErrorState description={errorMessage(roster.error)} onRetry={() => void roster.refetch()} />
  const { assignment: a, course } = roster.data
  const handedIn = roster.data.rows.filter((r) => r.submission).length
  const graded = roster.data.rows.filter((r) => r.submission?.gradedAt).length

  return (
    <>
      <PageHeader
        title={a.title}
        description={`${course.subject.name} · ${course.section.name}`}
        breadcrumbs={[
          { label: t('Online learning'), to: base },
          {
            label: `${course.subject.name} · ${course.section.name}`,
            to: `${base}/courses/${course.section.id}/${course.subject.id}`,
          },
          { label: a.title },
        ]}
        homeHref={portal === 'staff' ? '/staff' : '/admin'}
      />
      <div className="grid items-start gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader
            icon={<CalendarClock />}
            title={t('Due {{date}}', { date: formatDateTime(a.dueAt) })}
            description={[
              a.maxPoints ? t('Marked out of {{max}}', { max: a.maxPoints }) : t('Feedback only'),
              a.acceptLate ? t('Late work accepted') : t('No late work'),
            ].join(' · ')}
          />
          <CardBody className="space-y-3">
            {a.instructions ? <RichText text={a.instructions} /> : null}
            <FileChips files={a.files} />
          </CardBody>
        </Card>
        <Card className="lg:col-span-2">
          <CardHeader
            icon={<ClipboardList />}
            title={t('Hand-ins')}
            description={t('{{handedIn}} of {{total}} handed in · {{graded}} graded', {
              handedIn,
              total: roster.data.rows.length,
              graded,
            })}
          />
          <DataTable
            caption={t('Hand-ins for {{title}}', { title: a.title })}
            columns={columns}
            rows={roster.data.rows}
            getRowId={(r) => r.student.id}
            mobileCard={(r) => {
              const s = workStatusMeta[workStatus(a, r.submission)]
              return (
                <div className="flex items-center gap-3">
                  <Avatar name={r.student.name} src={r.student.photoUrl} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-ink">{r.student.name}</p>
                    <Badge tone={s.tone} size="sm">
                      {t(s.label)}
                    </Badge>
                  </div>
                  {r.submission ? (
                    <Button size="sm" variant="secondary" onClick={() => setGrading(r)}>
                      {r.submission.gradedAt ? formatPoints(r.submission.points, a.maxPoints) : t('Grade')}
                    </Button>
                  ) : null}
                </div>
              )
            }}
          />
        </Card>
      </div>
      {grading?.submission ? (
        <GradeDialog
          studentName={grading.student.name}
          submission={grading.submission}
          maxPoints={a.maxPoints}
          onOpenChange={(o) => !o && setGrading(null)}
        />
      ) : null}
    </>
  )
}
